import { useState, useEffect } from 'react';
import { Outlet, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useCompany } from '../../context/CompanyContext';
import { 
  LayoutDashboard, 
  Users, 
  FolderOpen, 
  Receipt, 
  DollarSign,
  Table2, 
  Settings,
  KeyRound,
  UserCheck,
  PieChart,
  BookOpen,
  TrendingUp,
  BarChart3,
  ChevronDown,
  ChevronRight,
  LogOut,
  User,
  Building2,
  Loader2,
  Menu,
  X,
  Wallet,
  CreditCard,
  Contact,
  Lock,
  Eye,
  EyeOff,
  Shield,
  Car,
  Gem,
  Landmark,
  Briefcase,
  TrendingDown,
  HandCoins,
  Home as HomeIcon,
  Calendar
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';

const NAV_ITEMS_BASE = [
  { path: '/home', label: 'Workspaces', icon: HomeIcon },
  { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/clients', label: 'Clients', icon: Users, dynamicLabel: true },
  { path: '/files', label: 'Files', icon: FolderOpen },
  { path: '/expenses', label: 'Expenses', icon: Receipt },
  { path: '/revenue', label: 'Revenue', icon: DollarSign },
  { path: '/passwords', label: 'Passwords', icon: KeyRound },
  { path: '/datasheets', label: 'Datasheets', icon: Table2 },
  { path: '/reports', label: 'Reports', icon: BarChart3 },
  { path: '/settings', label: 'Settings', icon: Settings },
];

function getClientsLabel(slug) {
  if (slug === 'veriquant') return 'Contact List';
  if (slug === 'saddlebrook') return 'Store Owners';
  return 'Contact List';
}

const VERIQUANT_NAV_ITEMS = [
  { path: '/employees', label: 'Employees', icon: UserCheck },
  { path: '/shareholders', label: 'Shareholders', icon: PieChart },
  { path: '/vesting', label: 'Vesting Schedule', icon: Calendar },
  { path: '/noteholders', label: 'Noteholders', icon: BookOpen },
  { path: '/vam-stats', label: 'VAM Stats', icon: TrendingUp },
];

const CABEX_NAV_ITEMS = [
  { path: '/cabex/clients', label: 'Clients', icon: Users },
  { path: '/cabex/reports', label: 'Reports', icon: BarChart3 },
];

const PRAXIS_NAV_ITEMS = [
  { path: '/praxis/dashboard', label: 'Praxis Dashboard', icon: LayoutDashboard },
  { path: '/praxis/clients', label: 'Clients', icon: Users },
];

const STUART_NAV_ITEMS = [
  { path: '/home', label: 'Workspaces', icon: HomeIcon },
  { path: '/personal', label: 'Dashboard', icon: LayoutDashboard },
  { 
    label: 'Assets', icon: Wallet, 
    children: [
      { path: '/personal/assets', label: 'Summary', icon: BarChart3 },
      { path: '/personal/assets/vehicles', label: 'Vehicles', icon: Car },
      { path: '/personal/assets/metals', label: 'Precious Metals', icon: Gem },
      { path: '/personal/assets/financial', label: 'Financial Accounts', icon: Landmark },
      { path: '/personal/assets/investments', label: 'Private Investments', icon: Briefcase },
    ]
  },
  { 
    label: 'Liabilities', icon: TrendingDown,
    children: [
      { path: '/personal/liabilities', label: 'Summary', icon: BarChart3 },
      { path: '/personal/liabilities/credit-cards', label: 'Credit Cards', icon: CreditCard },
      { path: '/personal/liabilities/loans', label: 'Loans', icon: HandCoins },
      { path: '/personal/liabilities/mortgages', label: 'Mortgages', icon: Landmark },
    ]
  },
  { path: '/personal/spending', label: 'Spending', icon: DollarSign },
  { path: '/personal/contacts', label: 'Contacts', icon: Contact },
  { path: '/personal/passwords', label: 'Passwords', icon: KeyRound },
  { path: '/personal/files', label: 'Files', icon: FolderOpen },
  { path: '/settings', label: 'Settings', icon: Settings },
];

function PinGate({ company, onVerified }) {
  const { verifyPin } = useCompany();
  const [pin, setPin] = useState('');
  const [showPin, setShowPin] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await verifyPin(company.id, pin);
      onVerified();
    } catch {
      setError('Incorrect password');
      setPin('');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center" data-testid="pin-gate">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-2xl bg-gray-900 flex items-center justify-center mx-auto mb-4">
            <Shield className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-semibold text-gray-900" style={{ fontFamily: 'Outfit' }}>
            {company.name}
          </h1>
          <p className="text-gray-500 mt-1 text-sm">Enter password to access</p>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm text-center" data-testid="pin-error">
              {error}
            </div>
          )}
          <div className="relative">
            <Input
              type={showPin ? 'text' : 'password'}
              value={pin}
              onChange={e => setPin(e.target.value)}
              placeholder="Enter password"
              className="h-12 rounded-xl pr-12"
              data-testid="pin-input"
              autoFocus
            />
            <button type="button" onClick={() => setShowPin(!showPin)} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
              {showPin ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
          <Button type="submit" disabled={loading || !pin} className="w-full h-12 rounded-xl" data-testid="pin-submit">
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Lock className="w-4 h-4 mr-2" />Unlock</>}
          </Button>
        </form>
      </div>
    </div>
  );
}

export default function DashboardLayout() {
  const { user, loading: authLoading, logout } = useAuth();
  const { companies, currentCompany, switchCompany, fetchCompanies, loading: companyLoading, isPinRequired } = useCompany();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [pinUnlocked, setPinUnlocked] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState({});

  const isPersonal = currentCompany?.slug === 'stuart';

  useEffect(() => {
    if (user) {
      fetchCompanies();
    }
  }, [user]);

  // Reset pin state when switching away from personal company
  useEffect(() => {
    if (currentCompany && !currentCompany.is_personal) {
      setPinUnlocked(false);
    }
  }, [currentCompany]);

  // Auto-switch active company when navigating to company-specific routes
  useEffect(() => {
    if (!companies || companies.length === 0) return;
    let targetSlug = null;
    if (location.pathname.startsWith('/praxis')) targetSlug = 'praxis-sigma';
    else if (location.pathname.startsWith('/cabex')) targetSlug = 'cabex';
    if (targetSlug && currentCompany?.slug !== targetSlug) {
      const target = companies.find((c) => c.slug === targetSlug);
      if (target) switchCompany(target);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, companies]);

  // Auto-expand menus based on current path
  useEffect(() => {
    if (isPersonal) {
      const expanded = {};
      for (const item of STUART_NAV_ITEMS) {
        if (item.children) {
          const match = item.children.some(c => location.pathname === c.path || location.pathname.startsWith(c.path + '/'));
          if (match) expanded[item.label] = true;
        }
      }
      setExpandedMenus(prev => ({ ...prev, ...expanded }));
    }
  }, [location.pathname, isPersonal]);

  const toggleMenu = (label) => {
    setExpandedMenus(prev => ({ ...prev, [label]: !prev[label] }));
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
          <p className="text-gray-500 text-sm">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (companyLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
          <p className="text-gray-500 text-sm">Loading...</p>
        </div>
      </div>
    );
  }

  // PIN gate for personal company
  if (currentCompany?.is_personal && isPinRequired(currentCompany) && !pinUnlocked) {
    return <PinGate company={currentCompany} onVerified={() => { setPinUnlocked(true); navigate('/personal'); }} />;
  }

  const isAdmin = (user || {}).role === 'admin';
  const isPraxisRoute = location.pathname.startsWith('/praxis');
  const isCabexRoute = location.pathname.startsWith('/cabex');
  const isPraxis = currentCompany?.slug === 'praxis-sigma' || isPraxisRoute;
  const isCabex = currentCompany?.slug === 'cabex' || isCabexRoute;
  const isVeriquant = currentCompany?.slug === 'veriquant' && !isPraxisRoute && !isCabexRoute;
  const isCaryPact = currentCompany?.slug === 'carypact';
  const baseItems = isPersonal ? STUART_NAV_ITEMS : NAV_ITEMS_BASE;
  const navItems = baseItems.filter((item) => {
    // Hide Passwords for non-admin users
    if (!isAdmin && (item.path === '/passwords' || item.path === '/personal/passwords')) return false;
    // On Praxis Sigma, hide all base nav items (only show Praxis-specific items below)
    if (isPraxis && item.path !== '/home') return false;
    // CaryPact: only Files and Passwords tabs
    if (isCaryPact && item.path !== '/files' && item.path !== '/passwords') return false;
    return true;
  });

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top Bar */}
      <header className="h-14 bg-white border-gray-200 border-b fixed top-0 left-0 right-0 z-50">
        <div className="h-full px-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden p-2 hover:bg-gray-100 rounded-lg"
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
            <button onClick={() => navigate('/home')} className="flex items-center gap-2 hover:opacity-80 transition-opacity" data-testid="logo-home-btn">
              <div className="w-8 h-8 rounded-lg bg-gray-900 flex items-center justify-center">
                <Building2 className="w-4 h-4 text-white" />
              </div>
              <span className="font-semibold text-gray-900 hidden sm:block">
                BusinessHub
              </span>
            </button>
          </div>

          {/* Company Switcher */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="gap-2 border-gray-200" data-testid="company-switcher">
                <div className="w-2 h-2 rounded-full" style={{ backgroundColor: currentCompany?.color || '#6366F1' }} />
                <span className="max-w-[120px] truncate">{currentCompany?.name || 'Select'}</span>
                <ChevronDown className="w-4 h-4 text-gray-400" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="center" className="w-48">
              {companies.map((company) => (
                <DropdownMenuItem
                  key={company.id}
                  onClick={() => {
                    switchCompany(company);
                    if (company.is_personal) navigate('/personal');
                    else if (company.slug === 'carypact') navigate('/files');
                    else navigate('/dashboard');
                  }}
                  className="gap-2 cursor-pointer"
                  data-testid={`company-option-${company.slug}`}
                >
                  <div className="w-2 h-2 rounded-full" style={{ backgroundColor: company.color }} />
                  <span>{company.name}</span>
                  {company.is_personal && <Lock className="w-3 h-3 text-gray-400 ml-auto" />}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* User Menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-2 p-2 rounded-lg hover:bg-gray-100" data-testid="user-menu">
                <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center">
                  <User className="w-4 h-4 text-gray-600" />
                </div>
                <span className="text-sm font-medium hidden sm:block text-gray-700">{user?.name}</span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <div className="px-2 py-1.5">
                <p className="text-sm font-medium">{user?.name}</p>
                <p className="text-xs text-gray-500">@{user?.username}</p>
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} className="text-red-600 cursor-pointer" data-testid="logout-btn">
                <LogOut className="w-4 h-4 mr-2" />Log out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      {/* Mobile Overlay */}
      {mobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 bg-black/20 z-40 top-14" onClick={() => setMobileMenuOpen(false)} />
      )}

      {/* Sidebar */}
      <aside className={`fixed top-14 left-0 bottom-0 w-56 bg-white border-gray-200 border-r z-40 transform transition-transform lg:translate-x-0 overflow-y-auto ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <nav className="p-3 space-y-1">
          {navItems.map((item) => {
            if (item.children) {
              const isExpanded = expandedMenus[item.label];
              const hasActiveChild = item.children.some(c => location.pathname === c.path || location.pathname.startsWith(c.path + '/'));
              return (
                <div key={item.label}>
                  <button
                    onClick={() => toggleMenu(item.label)}
                    data-testid={`nav-${item.label.toLowerCase()}`}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                      hasActiveChild ? 'text-gray-900 font-medium' : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    }`}
                  >
                    <item.icon className="w-5 h-5" />
                    <span className="flex-1 text-left">{item.label}</span>
                    {isExpanded ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
                  </button>
                  {isExpanded && (
                    <div className="ml-4 mt-0.5 space-y-0.5 border-l border-gray-200 pl-3">
                      {item.children.map((child) => {
                        const isActive = location.pathname === child.path;
                        return (
                          <button
                            key={child.path}
                            onClick={() => { navigate(child.path); setMobileMenuOpen(false); }}
                            data-testid={`nav-${child.label.toLowerCase().replace(/\s+/g, '-')}`}
                            className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-colors ${
                              isActive ? 'bg-gray-100 text-gray-900 font-medium' : 'text-gray-500 hover:bg-gray-50 hover:text-gray-900'
                            }`}
                          >
                            <child.icon className="w-4 h-4" />
                            <span>{child.label}</span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            }

            const displayLabel = item.dynamicLabel ? getClientsLabel(currentCompany?.slug) : item.label;
            const isActive = location.pathname === item.path || (item.path !== '/personal' && location.pathname.startsWith(item.path + '/'));
            return (
              <button
                key={item.path}
                onClick={() => { navigate(item.path); setMobileMenuOpen(false); }}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                  isActive
                    ? 'bg-gray-100 text-gray-900 font-medium'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                }`}
              >
                <item.icon className="w-5 h-5" />
                <span>{displayLabel}</span>
              </button>
            );
          })}

          {/* Veriquant-only items */}
          {isVeriquant && (
            <>
              <div className="pt-3 pb-1 px-3">
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Veriquant</p>
              </div>
              {VERIQUANT_NAV_ITEMS.map((item) => {
                const isActive = location.pathname === item.path || location.pathname.startsWith(item.path + '/');
                return (
                  <button
                    key={item.path}
                    onClick={() => { navigate(item.path); setMobileMenuOpen(false); }}
                    data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                      isActive ? 'bg-gray-100 text-gray-900 font-medium' : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    }`}
                  >
                    <item.icon className="w-5 h-5" />
                    <span>{item.label}</span>
                  </button>
                );
              })}
            </>
          )}

          {/* Cabex-only items */}
          {isCabex && (
            <>
              <div className="pt-3 pb-1 px-3">
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Cabex</p>
              </div>
              {CABEX_NAV_ITEMS.map((item) => {
                const isActive = location.pathname === item.path || location.pathname.startsWith(item.path + '/');
                return (
                  <button
                    key={item.path}
                    onClick={() => { navigate(item.path); setMobileMenuOpen(false); }}
                    data-testid={`nav-cabex-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                      isActive ? 'bg-gray-100 text-gray-900 font-medium' : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    }`}
                  >
                    <item.icon className="w-5 h-5" />
                    <span>{item.label}</span>
                  </button>
                );
              })}
            </>
          )}

          {/* Praxis-only items */}
          {isPraxis && (
            <>
              <div className="pt-3 pb-1 px-3">
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Praxis Sigma</p>
              </div>
              {PRAXIS_NAV_ITEMS.map((item) => {
                const isActive = location.pathname === item.path || location.pathname.startsWith(item.path + '/');
                return (
                  <button
                    key={item.path}
                    onClick={() => { navigate(item.path); setMobileMenuOpen(false); }}
                    data-testid={`nav-praxis-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                      isActive ? 'bg-gray-100 text-gray-900 font-medium' : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    }`}
                  >
                    <item.icon className="w-5 h-5" />
                    <span>{item.label}</span>
                  </button>
                );
              })}
            </>
          )}
        </nav>
      </aside>

      {/* Main Content */}
      <main className="lg:pl-56 pt-14 min-h-screen">
        <div className="p-6">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
